import React, { useEffect, useState } from "react";
import { api, meApi, photoboothGalleryApi, formatApiError, getToken } from "@/lib/api";
import { PageHeader, Card, Field, TextArea, SaveBar } from "./_shared";
import { ImageIcon, Download, Lock } from "lucide-react";

function AuthedImage({ src, alt }) {
  const [blobUrl, setBlobUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    (async () => {
      try {
        const token = getToken();
        const r = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const blob = await r.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);
  if (!blobUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black/60">
        <ImageIcon size={28} strokeWidth={1} className="text-white/25" />
      </div>
    );
  }
  return <img src={blobUrl} alt={alt} className="w-full h-full object-cover" loading="lazy" />;
}

async function downloadAuthed(url, filename) {
  try {
    const token = getToken();
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const blob = await r.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename || "photo.jpg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
  } catch { /* ignore */ }
}

export default function PhotoboothBrief() {
  const [data, setData] = useState({ impression_souhaitee: true });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  useEffect(() => {
    meApi.getPhotobooth().then((d) => setData({ impression_souhaitee: true, ...d })).catch(() => {});
    photoboothGalleryApi.myGallery().then(setPhotos).catch(() => {}).finally(() => setLoadingPhotos(false));
  }, []);
  const upd = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const downloadAllPhotos = async () => {
    if (photos.length === 0 || downloadingZip) return;
    setDownloadingZip(true);
    setErr("");
    setMsg("");
    try {
      // Use axios (auto-attaches token via interceptor) and force blob response.
      const resp = await api.get("/me/photobooth/gallery/zip", {
        responseType: "blob",
        validateStatus: () => true,
      });
      if (resp.status < 200 || resp.status >= 300) {
        // Try to surface the actual backend detail (blob → text → JSON)
        let detail = `Téléchargement impossible (HTTP ${resp.status}).`;
        try {
          const text = await resp.data.text();
          try {
            const j = JSON.parse(text);
            if (j?.detail) detail = j.detail;
          } catch {
            if (text && text.length < 300) detail = text;
          }
        } catch { /* keep default */ }
        setErr(detail);
        return;
      }
      // Success — extract filename from Content-Disposition
      const cd = resp.headers?.["content-disposition"] || "";
      const match = String(cd).match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : "photobooth.zip";
      const objectUrl = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      setMsg(`Téléchargement démarré : ${filename}`);
    } catch (e) {
      // Network-level failure (offline, DNS, CORS…)
      setErr(`Erreur réseau : ${e?.message || "connexion interrompue"}. Réessayez.`);
    } finally {
      setDownloadingZip(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setSaving(true);
    try {
      const r = await meApi.savePhotobooth(data);
      setData(r);
      setMsg("Préférences photobooth enregistrées.");
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="ec-photobooth">
      <PageHeader eyebrow="Photobooth" title="Mon" italic="Photobooth." desc="Personnalisez votre stand et retrouvez ici vos photos après l'événement." />

      {/* --- Ma galerie photobooth --- */}
      <Card className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <ImageIcon size={18} strokeWidth={1.4} className="text-[#DCB457]" />
            <h2 className="font-serif-luxe text-xl md:text-2xl">Ma galerie photobooth</h2>
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">({photos.length})</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {photos.length > 0 && (
              <button
                type="button"
                onClick={downloadAllPhotos}
                disabled={downloadingZip}
                data-testid="pb-download-all-btn"
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/40 hover:border-[#DCB457] hover:bg-[#DCB457]/10 px-3 py-1.5 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download size={11} strokeWidth={1.5} />
                {downloadingZip ? "Préparation du ZIP…" : "Télécharger toutes les photos"}
              </button>
            )}
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-emerald-300/85 border border-emerald-400/30 px-2 py-1">
              <Lock size={11} strokeWidth={1.5} /> Accès privé
            </span>
          </div>
        </div>

        {loadingPhotos ? (
          <p className="text-white/55 text-sm italic">Chargement de vos photos…</p>
        ) : photos.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-[#DCB457]/20">
            <ImageIcon size={30} strokeWidth={1} className="text-[#DCB457]/60 mx-auto mb-3" />
            <p className="text-white/65 text-sm">Aucune photo pour le moment.</p>
            <p className="text-white/40 text-xs mt-2">Vos photos apparaîtront ici après l'événement, dès que nous les aurons mises en ligne.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="pb-gallery-grid">
            {photos.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightbox(p)}
                data-testid={`pb-photo-${p.id}`}
                className="relative aspect-square border border-[#DCB457]/15 hover:border-[#DCB457]/50 transition group overflow-hidden"
              >
                <AuthedImage src={photoboothGalleryApi.myFileUrl(p.id)} alt={p.original_filename} />
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition" />
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="pb-lightbox"
        >
          <div className="relative max-w-5xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex-1 min-h-0 flex items-center justify-center">
              <AuthedImage src={photoboothGalleryApi.myFileUrl(lightbox.id)} alt={lightbox.original_filename} />
            </div>
            <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-white/55 truncate">{lightbox.original_filename}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => downloadAuthed(photoboothGalleryApi.myFileUrl(lightbox.id), lightbox.original_filename)}
                  data-testid="pb-lightbox-download"
                  className="text-[11px] uppercase tracking-[0.18em] text-[#DCB457] border border-[#DCB457]/40 px-3 py-1.5 hover:bg-[#DCB457]/10 inline-flex items-center gap-1.5"
                >
                  <Download size={11} strokeWidth={1.5} /> Télécharger
                </button>
                <button
                  type="button"
                  onClick={() => setLightbox(null)}
                  data-testid="pb-lightbox-close"
                  className="text-[11px] uppercase tracking-[0.18em] text-white/55 border border-white/15 px-3 py-1.5 hover:text-[#DCB457] hover:border-[#DCB457]/40"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Personnalisation du stand --- */}
      <Card>
        <h2 className="font-serif-luxe text-xl md:text-2xl mb-5">Personnalisation du stand</h2>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Formule choisie" value={data.formule} onChange={upd("formule")} placeholder="ex. Open Bar Photo 4h" testid="pb-formule" />
            <Field label="Thème" value={data.theme} onChange={upd("theme")} placeholder="ex. années 80, chic, festif…" testid="pb-theme" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Couleurs / Codes couleurs" value={data.couleurs} onChange={upd("couleurs")} testid="pb-couleurs" />
            <Field label="Fond souhaité" value={data.fond_souhaite} onChange={upd("fond_souhaite")} placeholder="ex. paillettes dorées, fleurs…" testid="pb-fond" />
          </div>
          <TextArea label="Accessoires souhaités" rows={3} value={data.accessoires} onChange={upd("accessoires")} placeholder="lunettes, chapeaux, panneaux personnalisés…" testid="pb-accessoires" />
          <label className="flex items-center gap-3 text-sm cursor-pointer">
            <input type="checkbox" checked={!!data.impression_souhaitee} onChange={(e) => upd("impression_souhaitee")(e.target.checked)} data-testid="pb-print" className="accent-[#DCB457]" />
            <span className="text-white/75">Impression instantanée des photos souhaitée</span>
          </label>
          <TextArea label="Notes complémentaires" rows={3} value={data.notes} onChange={upd("notes")} testid="pb-notes" />
          {err && <p className="text-xs text-red-300">{err}</p>}
          <SaveBar saving={saving} message={msg} />
        </form>
      </Card>
    </div>
  );
}

